import { useMemo } from 'react';
import { Box } from '@rsos/components/capstone/base';
import {
  type KeyOfChatComponentType,
  useChatComponents,
} from '../useChatComponents';
import AgencyShareChatBox from './AgencyShareChat/ASCChatBox';
import { ChatContextProvider, ChatType } from './ChatContext';
import CollaboratorChatbox from './CollaboratorChatBox/CollaboratorChatbox';
import ConversationsChatBox from './ConversationsChatBox/ConversationsChatbox';
import RcsChatBox from './RcsChatBox/RcsChatBox';
import TF911ChatBox from './TF911ChatBox/TF911ChatBox';

const CHAT_CONFIG: Record<
  KeyOfChatComponentType,
  { chatType: ChatType; Component: React.ComponentType }
> = {
  conversations: { chatType: 'conversations', Component: ConversationsChatBox },
  rcs: { chatType: 'rcs', Component: RcsChatBox },
  tf911: { chatType: 'textFrom911', Component: TF911ChatBox },
};

const CallChats = () => {
  const {
    chatComponents,
    shouldRenderAgencyShareChat,
    shouldRenderCollaboratorChat,
    soloActiveChat,
  } = useChatComponents();

  const visibleChats = useMemo(() => {
    const keys = Object.keys(chatComponents);
    const nonNullKeys = keys.filter(
      key => chatComponents[key as KeyOfChatComponentType] !== null,
    );

    // If soloActiveChat is set to a specific key (exactly 1 active, or createdAt fallback),
    // show only that key
    if (soloActiveChat !== null) {
      return nonNullKeys.filter(key => key === soloActiveChat);
    }

    // soloActiveChat is null — means either multiple active or no entries at all.
    // Show only the active ones.
    return nonNullKeys.filter(
      key => chatComponents[key as KeyOfChatComponentType]?.isActive === true,
    );
  }, [chatComponents, soloActiveChat]);

  return (
    <Box data-name="call-chats" height="100%" overflowY="auto" gridRowGap="8px">
      {visibleChats.map(key => {
        const { chatType, Component } =
          CHAT_CONFIG[key as KeyOfChatComponentType];
        return (
          <ChatContextProvider key={key} chatType={chatType}>
            <Component />
          </ChatContextProvider>
        );
      })}
      {shouldRenderCollaboratorChat && (
        <ChatContextProvider chatType="collaborator">
          <CollaboratorChatbox />
        </ChatContextProvider>
      )}
      {shouldRenderAgencyShareChat && (
        <ChatContextProvider chatType="agencyShare">
          <AgencyShareChatBox />
        </ChatContextProvider>
      )}
    </Box>
  );
};

export default CallChats;
